import { useMemo } from "react";

import {
    hasActiveEarnPosition,
    useEarnPortfolioPositions,
    useLoopscaleWalletContext,
    useOraclePrices,
    useActivePortfolioRewards,
    useTokenListContext
} from "@bridgesplit/abf-react";
import { TableKey } from "@bridgesplit/ui";
import { AllValueType, isAll, TIME, tokenAmountToUiNumber } from "@bridgesplit/utils";
import { buildEarnPositionView, EarnPortfolioPositionView } from "app/utils/domain/earn";

export type { EarnPortfolioPositionView };

// Earn has no server-paginated positions endpoint; `/earn/portfolio/positions` returns the wallet's full
// flat list, enriched app-side into EarnPortfolioPositionView. This slices that list into the requested
// chunk client-side, mirroring the { positions, totalCount } shape of the server-paginated loan/loop hooks.
export function useEarnPositionsPaginated({
    pagination,
    depositGroupKey,
    skip
}: {
    pagination: { page: number; pageSize: number };
    depositGroupKey: AllValueType | TableKey;
    skip?: boolean;
}): {
    positions: EarnPortfolioPositionView[] | undefined;
    totalCount: number | undefined;
    isLoading: boolean;
    isFetching: boolean;
} {
    const { positions, isLoading } = useEarnPortfolioPositionsView(!skip);
    const depositMint = isAll(depositGroupKey) ? undefined : String(depositGroupKey);

    const filtered = useMemo(() => {
        if (!positions) return undefined;
        if (!depositMint) return positions;
        return positions.filter((position) => position.response.vault.acceptedAssetMint === depositMint);
    }, [positions, depositMint]);

    const chunk = useMemo(() => {
        if (!filtered) return undefined;
        const start = pagination.page * pagination.pageSize;
        return filtered.slice(start, start + pagination.pageSize);
    }, [filtered, pagination.page, pagination.pageSize]);

    // The view already folds isFetching into isLoading, so mirror it for the shared paginated shape.
    return { positions: chunk, totalCount: filtered?.length, isLoading, isFetching: isLoading };
}

export function useEarnPortfolioPositionsView(enabled = true): {
    positions: EarnPortfolioPositionView[] | undefined;
    totalValueUsd: number | undefined;
    totalCostBasisUsd: number | undefined;
    totalUnrealizedPnlUsd: number | undefined;
    totalRealizedPnlUsd: number | undefined;
    fulfilledUnclaimedUi: number | undefined;
    fulfilledUnclaimedUsd: number | undefined;
    fulfilledUnclaimedVaultCount: number;
    totalBaseApy: number | undefined;
    totalApy: number | undefined;
    totalRewardApy: number | undefined;
    expectedDailyYieldUsd: number | undefined;
    totalDailyPoints: number | undefined;
    isLoading: boolean;
} {
    const {
        data: { userWalletAddress: activeWallet }
    } = useLoopscaleWalletContext();
    const { getMetadata } = useTokenListContext();
    const positions = useEarnPortfolioPositions(enabled ? activeWallet : undefined);
    const acceptedMints = useMemo(
        () => Array.from(new Set(positions.data?.map((p) => p.vault.acceptedAssetMint) ?? [])),
        [positions.data]
    );
    const { getUsdPrice, isLoading: pricesLoading } = useOraclePrices(acceptedMints);
    const { data: portfolioRewards, isLoading: portfolioRewardsLoading } = useActivePortfolioRewards(!enabled);

    const positionViews = useMemo(() => {
        return positions.data?.filter(hasActiveEarnPosition).map((response): EarnPortfolioPositionView => {
            const view = buildEarnPositionView(response, { getMetadata, getUsdPrice });
            const pointsReward = portfolioRewards?.earnVaults?.[response.vault.address];
            const pointsPerSecond = pointsReward?.baseReward.pointsPerSecond;
            return {
                ...view,
                dailyPoints: pointsPerSecond === undefined ? undefined : pointsPerSecond * TIME.DAY,
                pointsReward
            };
        });
    }, [getMetadata, getUsdPrice, portfolioRewards, positions.data]);

    const isLoading = positions.isLoading || positions.isFetching || pricesLoading || portfolioRewardsLoading;
    const totalValueUsd = useMemo(() => {
        if (isLoading || !positionViews) return undefined;
        return positionViews.reduce((sum, position) => sum + (position.valueUsd ?? 0), 0);
    }, [isLoading, positionViews]);
    const totalCostBasisUsd = useMemo(() => {
        if (isLoading || !positionViews) return undefined;
        return positionViews.reduce((sum, position) => sum + (position.costBasisUsd ?? 0), 0);
    }, [isLoading, positionViews]);
    const totalUnrealizedPnlUsd = useMemo(() => {
        if (isLoading || !positionViews) return undefined;
        return positionViews.reduce((sum, position) => sum + (position.unrealizedPnlUsd ?? 0), 0);
    }, [isLoading, positionViews]);
    const totalRealizedPnlUsd = useMemo(() => {
        if (isLoading || !positionViews) return undefined;
        return positionViews.reduce((sum, position) => sum + (position.realizedPnlUsd ?? 0), 0);
    }, [isLoading, positionViews]);
    const fulfilledUnclaimedUi = useMemo(() => {
        if (isLoading || !positionViews) return undefined;
        return positionViews.reduce(
            (sum, position) => sum + (tokenAmountToUiNumber(position.fulfilledUnclaimed) ?? 0),
            0
        );
    }, [isLoading, positionViews]);
    const fulfilledUnclaimedUsd = useMemo(() => {
        if (isLoading || !positionViews) return undefined;
        return positionViews.reduce((sum, position) => sum + (position.fulfilledUnclaimedUsd ?? 0), 0);
    }, [isLoading, positionViews]);
    const fulfilledUnclaimedVaultCount = useMemo(
        () => positionViews?.filter((position) => position.fulfilledUnclaimedCount > 0).length ?? 0,
        [positionViews]
    );
    const apyTotals = useMemo(() => {
        if (isLoading || !positionViews) return undefined;
        const totalValue = positionViews.reduce((sum, position) => sum + (position.valueUsd ?? 0), 0);
        if (totalValue <= 0) return undefined;
        if (positionViews.some((position) => position.headlineApy === undefined)) return undefined;
        return positionViews.reduce(
            (sum, position) => {
                const weight = (position.valueUsd ?? 0) / totalValue;
                return {
                    baseApy: sum.baseApy + weight * (position.baseApy ?? 0),
                    rewardApy: sum.rewardApy + weight * (position.rewardApy ?? 0),
                    apy: sum.apy + weight * (position.headlineApy ?? 0)
                };
            },
            { baseApy: 0, rewardApy: 0, apy: 0 }
        );
    }, [isLoading, positionViews]);
    const expectedDailyYieldUsd = useMemo(() => {
        if (isLoading || !positionViews) return undefined;
        if (positionViews.some((position) => position.headlineApy === undefined)) return undefined;
        return positionViews.reduce((sum, position) => {
            const apy = position.headlineApy ?? 0;
            return sum + ((position.valueUsd ?? 0) * apy) / 365;
        }, 0);
    }, [isLoading, positionViews]);
    const totalDailyPoints = useMemo(() => {
        if (isLoading || !positionViews) return undefined;
        if (positionViews.some((position) => position.dailyPoints === undefined)) return undefined;
        return positionViews.reduce((sum, position) => sum + (position.dailyPoints ?? 0), 0);
    }, [isLoading, positionViews]);

    return {
        positions: positionViews,
        totalValueUsd,
        totalCostBasisUsd,
        totalUnrealizedPnlUsd,
        totalRealizedPnlUsd,
        fulfilledUnclaimedUi,
        fulfilledUnclaimedUsd,
        fulfilledUnclaimedVaultCount,
        totalBaseApy: apyTotals?.baseApy,
        totalApy: apyTotals?.apy,
        totalRewardApy: apyTotals?.rewardApy,
        expectedDailyYieldUsd,
        totalDailyPoints,
        isLoading
    };
}
